import { useLayoutEffect, useRef, type ComponentProps } from "react";

import { cn } from "@/shared/lib/cn";
import { assignRef } from "@/shared/lib/refs";
import { inputClassName } from "@/shared/ui/styles";

interface TextareaProps extends Omit<ComponentProps<"textarea">, "rows"> {
  /** Height when empty, in lines; it grows with the text from there. */
  minRows?: number;
}

/** Fit the box to its text: no inner scrollbar until a max-height (from className) is reached. */
function fitToContent(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  const borders = textarea.offsetHeight - textarea.clientHeight;
  textarea.style.height = `${String(textarea.scrollHeight + borders)}px`;
  textarea.style.overflowY = textarea.scrollHeight > textarea.clientHeight ? "auto" : "hidden";
}

/**
 * A multi-line field in the Input look that grows as people type. Text direction follows what
 * is typed (dir="auto"). Works controlled or with react-hook-form's `register`.
 */
export function Textarea({ minRows = 3, className, ref, onChange, ...props }: TextareaProps) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  // Also after renders without typing, such as a form reset or a new value from the server.
  useLayoutEffect(() => {
    if (innerRef.current) fitToContent(innerRef.current);
  });

  return (
    <textarea
      dir="auto"
      rows={minRows}
      ref={(node) => {
        innerRef.current = node;
        assignRef(ref, node);
      }}
      onChange={(event) => {
        fitToContent(event.currentTarget);
        onChange?.(event);
      }}
      className={cn(inputClassName, "h-auto resize-none py-2 leading-relaxed", className)}
      {...props}
    />
  );
}
